import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft,
  Edit3,
  Plus,
  Power,
  Scale,
  Sparkles,
} from 'lucide-react';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import UnitConversionFormModal from './UnitConversionFormModal';
import UnitConversionTable from './components/UnitConversionTable';
import UnitConversionFilters from './components/UnitConversionFilters';
import useRestaurantUnitConversions from '../../../hooks/restaurant/useRestaurantUnitConversions';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import ProductUnitService from '../../../services/ProductUnitService';
import { IProductUnit } from '../../../types/productUnit/productUnitInterfaces';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import { RestaurantUnitConversionFilters } from '../../../types/restaurant/RestaurantUnitConversionTypes';
import { RestaurantPageRequest } from '../../../types/restaurant/RestaurantRecipeTypes';

const RestaurantUnitConversionsScreen = () => {
  const [units, setUnits] = useState<IProductUnit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [unitError, setUnitError] = useState('');

  const [search, setSearch] = useState('');
  const [rawMaterialId, setRawMaterialId] = useState(0);
  const [fromUnitId, setFromUnitId] = useState(0);
  const [toUnitId, setToUnitId] = useState(0);
  const [active, setActive] = useState<boolean | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort] = useState(['id,asc']);
  const debouncedSearch = useDebouncedValue(search, 350);

  const conversions = useRestaurantUnitConversions();

  useEffect(() => {
    const loadUnits = async () => {
      setLoadingUnits(true);
      setUnitError('');

      try {
        const response = await ProductUnitService.getDropdown();
        setUnits(response?.data ?? response ?? []);
      } catch (error: any) {
        setUnitError(
          error?.response?.data?.message ||
            'No fue posible cargar las unidades.'
        );
      } finally {
        setLoadingUnits(false);
      }
    };

    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const request = useMemo<
    RestaurantPageRequest<RestaurantUnitConversionFilters>
  >(
    () => ({
      page,
      size,
      sort,
      filters: {
        search: debouncedSearch.trim() || null,
        rawMaterialId: rawMaterialId || null,
        fromUnitId: fromUnitId || null,
        toUnitId: toUnitId || null,
        active,
      },
    }),
    [
      active,
      debouncedSearch,
      fromUnitId,
      page,
      rawMaterialId,
      size,
      sort,
      toUnitId,
    ]
  );

  useEffect(() => {
    conversions.load(request);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const activeCount = conversions.list.filter((item) => item.active).length;
  const globalCount = conversions.list.filter((item) => item.global).length;

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <div className="overflow-hidden rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke bg-white px-5 py-5 dark:border-strokedark dark:bg-boxdark sm:px-7">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                  <ArrowRightLeft className="h-5 w-5" />
                </span>

                <div>
                  <h1 className="text-xl font-semibold text-black dark:text-white">
                    Conversiones de unidad
                  </h1>

                  <p className="mt-1 max-w-2xl text-sm text-body dark:text-bodydark">
                    Configure equivalencias entre unidades para compras, recetas
                    e inventario de restaurante.
                  </p>
                </div>
              </div>

              <button
                onClick={conversions.openCreate}
                disabled={loadingUnits || units.length === 0}
                className="
                  inline-flex h-12 items-center justify-center gap-2 rounded-lg
                  bg-primary px-5 text-sm font-medium text-white shadow-1
                  transition hover:bg-opacity-90 disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                <Plus className="h-5 w-5" />
                Nueva conversión
              </button>
            </div>

            {unitError && (
              <div className="mt-4 rounded-lg border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
                {unitError}
              </div>
            )}
          </div>

          {!loadingUnits && !unitError && units.length === 0 ? (
            <div className="flex min-h-[340px] flex-col items-center justify-center px-6 text-center">
              <span className="mb-4 rounded-full bg-gray-2 p-5 text-body dark:bg-meta-4 dark:text-bodydark">
                <Scale className="h-10 w-10" />
              </span>

              <h2 className="text-lg font-semibold text-black dark:text-white">
                No hay unidades disponibles
              </h2>

              <p className="mt-2 max-w-md text-sm text-body dark:text-bodydark">
                Registre unidades de producto antes de crear conversiones.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 border-b border-stroke bg-gray-2/60 p-5 dark:border-strokedark dark:bg-boxdark-2 sm:grid-cols-3">
                <div className="rounded-xl border border-stroke bg-white p-4 shadow-1 dark:border-strokedark dark:bg-boxdark">
                  <p className="text-xs font-semibold uppercase tracking-wide text-bodydark2">
                    Registradas
                  </p>

                  <p className="mt-2 text-2xl font-bold text-black dark:text-white">
                    {conversions.page?.page.totalElements ?? 0}
                  </p>
                </div>

                <div className="rounded-xl border border-stroke bg-white p-4 shadow-1 dark:border-strokedark dark:bg-boxdark">
                  <p className="text-xs font-semibold uppercase tracking-wide text-bodydark2">
                    Activas en página
                  </p>

                  <p className="mt-2 text-2xl font-bold text-success">
                    {activeCount}
                  </p>
                </div>

                <div className="rounded-xl border border-stroke bg-white p-4 shadow-1 dark:border-strokedark dark:bg-boxdark">
                  <p className="text-xs font-semibold uppercase tracking-wide text-bodydark2">
                    Globales en página
                  </p>

                  <p className="mt-2 text-2xl font-bold text-primary">
                    {globalCount}
                  </p>
                </div>
              </div>

              <UnitConversionFilters
                units={units}
                search={search}
                fromUnitId={fromUnitId}
                toUnitId={toUnitId}
                rawMaterialId={rawMaterialId}
                status={
                  active === null ? 'ALL' : active ? 'ACTIVE' : 'INACTIVE'
                }
                onSearchChange={(value) => {
                  setSearch(value);
                  setPage(0);
                }}
                onFromUnitChange={(value) => {
                  setFromUnitId(value);
                  setPage(0);
                }}
                onToUnitChange={(value) => {
                  setToUnitId(value);
                  setPage(0);
                }}
                onRawMaterialChange={(value) => {
                  setRawMaterialId(value);
                  setPage(0);
                }}
                onStatusChange={(value) => {
                  setActive(value === 'ALL' ? null : value === 'ACTIVE');
                  setPage(0);
                }}
              />

              {conversions.loading || loadingUnits ? (
                <div className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-2 xl:grid-cols-3">
                  {[1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="h-52 animate-pulse rounded-xl bg-gray-2 dark:bg-meta-4"
                    />
                  ))}
                </div>
              ) : conversions.list.length === 0 ? (
                <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center">
                  <span className="mb-4 rounded-full bg-primary/10 p-5 text-primary">
                    <ArrowRightLeft className="h-9 w-9" />
                  </span>

                  <h3 className="text-lg font-semibold text-black dark:text-white">
                    No hay conversiones para mostrar
                  </h3>

                  <p className="mt-2 max-w-md text-sm text-body dark:text-bodydark">
                    Cree equivalencias como kilogramo a gramo, litro a mililitro
                    o caja a unidad.
                  </p>
                </div>
              ) : (
                <>
                <UnitConversionTable
                  conversions={conversions.list}
                  onEdit={conversions.edit}
                  onDeactivate={(item) =>
                    conversions.deactivate(item, request, () =>
                      setPage((current) => Math.max(0, current - 1))
                    )
                  }
                />
                <div className="grid grid-cols-1 gap-4 p-5 md:hidden">
                  {conversions.list.map((item) => (
                    <article
                      key={item.id}
                      className={`
                        rounded-xl border border-stroke bg-white p-5 shadow-1
                        transition hover:-translate-y-0.5 hover:border-primary/40
                        hover:shadow-default dark:border-strokedark dark:bg-boxdark
                        ${item.active ? '' : 'opacity-65'}
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <Sparkles className="h-5 w-5" />
                          </span>

                          <div className="min-w-0">
                            <h3 className="truncate font-semibold text-black dark:text-white">
                              {item.fromUnitName} → {item.toUnitName}
                            </h3>

                            <p className="mt-1 text-sm text-body dark:text-bodydark">
                              1 {item.fromUnitCode || item.fromUnitName} ={' '}
                              <span className="font-semibold text-black dark:text-white">
                                {item.factor}
                              </span>{' '}
                              {item.toUnitCode || item.toUnitName}
                            </p>
                          </div>
                        </div>

                        <span
                          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            item.active
                              ? 'bg-success/10 text-success'
                              : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {item.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                          {item.global ? 'Global' : 'Materia prima'}
                        </span>

                        {!item.global && item.rawMaterialName && (
                          <span className="rounded-full bg-gray-2 px-2.5 py-1 text-xs font-medium text-body dark:bg-meta-4 dark:text-bodydark">
                            {item.rawMaterialName}
                          </span>
                        )}
                      </div>

                      <div className="mt-5 flex gap-2 border-t border-stroke pt-4 dark:border-strokedark">
                        <button
                          onClick={() => conversions.edit(item)}
                          className="
                            inline-flex flex-1 items-center justify-center gap-2
                            rounded-lg border border-stroke px-3 py-2 text-sm
                            font-medium text-black transition hover:border-primary
                            hover:bg-primary/5 hover:text-primary
                            dark:border-strokedark dark:text-white
                          "
                        >
                          <Edit3 className="h-4 w-4" />
                          Editar
                        </button>

                        {item.active && (
                          <button
                            onClick={() =>
                              conversions.deactivate(item, request, () =>
                                setPage((current) => Math.max(0, current - 1))
                              )
                            }
                            title="Desactivar conversión"
                            className="
                              rounded-lg border border-stroke p-2 text-body
                              transition hover:border-danger hover:bg-danger/5
                              hover:text-danger dark:border-strokedark
                              dark:text-bodydark
                            "
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
                </>
              )}
              <div className="pb-5">
                <BasicPagination
                  currentPage={conversions.page?.page.number ?? 0}
                  totalPages={conversions.page?.page.totalPages ?? 0}
                  totalElements={conversions.page?.page.totalElements ?? 0}
                  size={conversions.page?.page.size ?? size}
                  first={conversions.page ? conversions.page.page.number === 0 : true}
                  last={conversions.page ? conversions.page.page.number >= conversions.page.page.totalPages - 1 : true}
                  onPageChange={setPage}
                  onSizeChange={(value) => {
                    setSize(value);
                    setPage(0);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </SectionWrapper>

      <UnitConversionFormModal
        isOpen={conversions.modalOpen}
        form={conversions.form}
        saving={conversions.saving}
        units={units}
        onClose={conversions.closeModal}
        onChange={conversions.updateField}
        onSave={() =>
          conversions.save(request, () =>
            setPage((current) => Math.max(0, current - 1))
          )
        }
      />
    </WorkspaceLayout>
  );
};

export default RestaurantUnitConversionsScreen;
